import { UnroutedExchangeSummary } from '@chiroup/core/types/ConnectorAdmin.type';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import connectionApiService, {
  ConnectionApiConsole,
  UnroutedDispositionChangeResult,
} from '../services/connectionApi.service';

const KEY_BASE = ['connection-api'] as const;
/** ONE query paints the console; every mutation invalidates it. */
const CONSOLE_KEY = [...KEY_BASE, 'console'] as const;

/**
 * Invalidate the ONE console query (key stays module-private). For the rare
 * spots that need a re-read outside a mutation — e.g. TracePanel's
 * natural-expiry timeout flipping the session label off.
 */
export const useInvalidateConsole = () => {
  const queryClient = useQueryClient();

  return () => queryClient.invalidateQueries(CONSOLE_KEY);
};

export const useConnectionApiConsole = () =>
  useQuery(CONSOLE_KEY, () => connectionApiService.consoleStatus(), {
    refetchOnWindowFocus: false,
    retry: false,
  });

/** State-changing ops re-read the (single) console query. */
const useConsoleMutation = <TArgs, TResult>(fn: (args: TArgs) => Promise<TResult>) => {
  const queryClient = useQueryClient();

  return useMutation<TResult, unknown, TArgs>(fn, {
    onSuccess: () => queryClient.invalidateQueries(CONSOLE_KEY),
  });
};

const useVendorsMutation = useConsoleMutation;

export const useRunCredentialCheck = () =>
  useVendorsMutation((vendor?: string) => connectionApiService.runCredentialCheck(vendor));

export const useStageCandidate = () =>
  useVendorsMutation(
    ({
      vendor,
      integrationId,
      apiKey,
    }: {
      vendor: string;
      integrationId: number;
      apiKey: string;
    }) => connectionApiService.stageCandidate(vendor, integrationId, apiKey)
  );

export const useValidateCandidate = () =>
  useVendorsMutation(({ vendor, integrationId }: { vendor: string; integrationId: number }) =>
    connectionApiService.validateCandidate(vendor, integrationId)
  );

export const usePromoteCandidate = () =>
  useVendorsMutation(({ vendor, integrationId }: { vendor: string; integrationId: number }) =>
    connectionApiService.promoteCandidate(vendor, integrationId)
  );

export const useRollbackCredentials = () =>
  useVendorsMutation(({ vendor, integrationId }: { vendor: string; integrationId: number }) =>
    connectionApiService.rollback(vendor, integrationId)
  );

const UNROUTED_KEY = [...KEY_BASE, 'unrouted'] as const;

export const useUnroutedExchanges = () =>
  useQuery(UNROUTED_KEY, () => connectionApiService.listUnrouted(), {
    refetchOnWindowFocus: false,
  });

/** Assign REMOVES the row — it leaves the queue for a clinic. Patch, don't refetch. */
const useUnroutedRemoval = <TArgs extends { exchangeId: string }, TResult>(
  fn: (args: TArgs) => Promise<TResult>
) => {
  const queryClient = useQueryClient();

  return useMutation<TResult, unknown, TArgs>(fn, {
    onSuccess: (_data, args) => {
      queryClient.setQueryData<{ rows: UnroutedExchangeSummary[] } | undefined>(
        UNROUTED_KEY,
        prev => (prev ? { rows: prev.rows.filter(r => r.exchangeId !== args.exchangeId) } : prev)
      );
    },
  });
};

export const useAssignUnrouted = () =>
  useUnroutedRemoval(({ exchangeId, clinicId }: { exchangeId: string; clinicId: number }) =>
    connectionApiService.assignUnrouted(exchangeId, clinicId)
  );

/**
 * Close/reopen do NOT remove — the row STAYS in the queue and just changes
 * disposition (Unrouted ⇄ Resolved). Patch the cached row's disposition +
 * history straight from the response (no invalidate-and-refetch).
 */
const useUnroutedDispositionChange = <TArgs extends { exchangeId: string }>(
  fn: (args: TArgs) => Promise<UnroutedDispositionChangeResult>
) => {
  const queryClient = useQueryClient();

  return useMutation<UnroutedDispositionChangeResult, unknown, TArgs>(fn, {
    onSuccess: data => {
      queryClient.setQueryData<{ rows: UnroutedExchangeSummary[] } | undefined>(
        UNROUTED_KEY,
        prev =>
          prev
            ? {
                rows: prev.rows.map(r =>
                  r.exchangeId === data.exchangeId
                    ? { ...r, disposition: data.disposition, history: [...r.history, data.change] }
                    : r
                ),
              }
            : prev
      );
    },
  });
};

export const useCloseUnrouted = () =>
  useUnroutedDispositionChange(({ exchangeId, reason }: { exchangeId: string; reason: string }) =>
    connectionApiService.closeUnrouted(exchangeId, reason)
  );

export const useReopenUnrouted = () =>
  useUnroutedDispositionChange(({ exchangeId, message }: { exchangeId: string; message: string }) =>
    connectionApiService.reopenUnrouted(exchangeId, message)
  );

export const useRunTransmitBackstop = () =>
  useConsoleMutation((vendor?: string) => connectionApiService.transmitRun(vendor));

/** Per-vendor "run inbound drain now" — kicks the drain; refresh shows results. */
export const useRunInboundDrain = () =>
  useConsoleMutation((vendor: string) => connectionApiService.inboundDrainRun(vendor));

/** Turn automation polling on/off for the caller's own combo (refreshes the console). */
export const useTogglePolling = () =>
  useConsoleMutation((args: { vendor: string; enabled: boolean }) =>
    connectionApiService.togglePolling(args.vendor, args.enabled)
  );

export const useClearTransmitLock = () =>
  useConsoleMutation((vendor: string) => connectionApiService.clearTransmitLock(vendor));

/** Re-drive a clinicId-0 inbound row (fetch-stub retry, INFRA-6). */
export const useRedispatchInbound = () =>
  useConsoleMutation((exchangeId: string) => connectionApiService.redispatchInbound(exchangeId));

export const useSetTraceConfig = (vendor?: string, integrationId?: number) => {
  const queryClient = useQueryClient();

  return useMutation(
    (body: Parameters<typeof connectionApiService.setTraceConfig>[2]) =>
      connectionApiService.setTraceConfig(vendor!, integrationId!, body),
    {
      // The response IS the fresh config — patch it into the console payload
      // rather than invalidate-and-refetch.
      onSuccess: data => {
        queryClient.setQueryData<ConnectionApiConsole | undefined>(CONSOLE_KEY, prev =>
          prev && vendor
            ? { ...prev, traceConfigs: { ...prev.traceConfigs, [vendor]: data } }
            : prev
        );
      },
    }
  );
};
